import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import cloudinary from 'cloudinary-core';
import VisibilitySensor from 'react-visibility-sensor';
import debounce from 'lodash/debounce';

import cloudinaryConfig from 'marketing-website/configs/cloudinary.js';
import propTypeEmptyString from 'marketing-website/utils/customPropTypesCheck.js';

import generalStyles from './style/general.module.css';

const defaultVideoWidth = 1920;

export default class SmartVideo extends Component {
  constructor() {
    super();

    this.state = {
      videoWidth: defaultVideoWidth,
    };

    this.cl = cloudinary.Cloudinary.new({
      cloud_name: cloudinaryConfig.cloudName,
      secure: true,
    });

    this.handleVisibility = this.handleVisibility.bind(this);
    this.handleResize = debounce(this.handleResize.bind(this), 400);
  }

  componentDidMount() {
    this.setVideoWidth();
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    this.setVideoWidth();
  }

  videoRef = videoRef => {
    this.video = videoRef;
  };

  handleVisibility(isVisible) {
    const { autoPlay } = this.props;

    if (autoPlay && this.video && isVisible) {
      // https://developers.google.com/web/updates/2017/06/play-request-was-interrupted
      // We can ignore the exception, The play() request can be safely interrupted
      // when navigating to another page
      this.video.play();
    } else {
      this.video.pause();
    }
  }

  setVideoWidth() {
    const { maxWidth } = this.props;
    const { videoWidth } = this.state;
    const windowWidth = window.innerWidth;

    let newWidth = windowWidth;

    // Check if video can't be bigger then the window
    if (newWidth > maxWidth) {
      newWidth = maxWidth;
    }

    // Don't reload the video as the width didn't change
    if (videoWidth === newWidth) {
      return;
    }

    // Reload the video, to reflect the new size
    this.setState({ videoWidth: newWidth }, () => {
      this.video.load();
    });
  }

  render() {
    const { videoWidth } = this.state;
    const {
      className,
      controls,
      loop,
      autoPlay,
      customPoster,
      muted,
      name,
      quality,
      borderRadius,
    } = this.props;

    let videoPoster = customPoster;

    if (videoPoster === '') {
      videoPoster = this.cl.url(`${name}.jpg`, {
        resource_type: 'video',
        quality: 50,
        responsive: true,
      });
    }

    let dpr = 1;
    if (typeof window !== 'undefined') {
      dpr = window.devicePixelRatio;
    }

    const videoUrl = this.cl.url(name, {
      crop: 'scale',
      quality,
      dpr,
      resource_type: 'video',
      width: videoWidth,
    });

    const videoProps = {
      className: cx(generalStyles.video, className),
      controls,
      loop,
      muted,
      autoPlay,
      poster: videoPoster,
    };

    const visibilitySensorProps = {
      onChange: this.handleVisibility,
      partialVisibility: true,
    };

    return (
      <VisibilitySensor {...visibilitySensorProps}>
        <video style={{ borderRadius }} {...videoProps} ref={this.videoRef}>
          <source src={videoUrl} />
        </video>
      </VisibilitySensor>
    );
  }
}

SmartVideo.propTypes = {
  autoPlay: PropTypes.bool,
  className: PropTypes.string,
  controls: PropTypes.bool,
  loop: PropTypes.bool,
  muted: PropTypes.bool,
  name: propTypeEmptyString,
  customPoster: PropTypes.string,
  maxWidth: PropTypes.number,
  quality: PropTypes.number,
  borderRadius: PropTypes.number,
};

SmartVideo.defaultProps = {
  autoPlay: false,
  maxWidth: defaultVideoWidth,
  quality: 90,
  muted: true,
  borderRadius: 0,
  customPoster: '',
};



// WEBPACK FOOTER //
// ./src/components/modules/SmartVideo/index.js